/**
 * DemoCountdownBanner — Vague 113 Wave 1m
 *
 * Slim top-of-app banner shown to demo users with:
 *   - Days left countdown (with color escalation)
 *   - CTA "Demander une prolongation"
 *   - CTA "Contacter pour s'abonner" (mailto)
 *
 * Hides when user is not on a demo org. Polls /api/demo/status every 5 min.
 */
import React, { useEffect, useState } from 'react';
import { Clock, MailPlus, AlertCircle, X, Loader2 } from 'lucide-react';
import api from '../api/client';
import { toast } from '../hooks/use-toast';

const DISMISS_KEY = 'ofm_demo_banner_dismissed_until';

export default function DemoCountdownBanner() {
  const [status, setStatus] = useState(null);
  const [requesting, setRequesting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get('/demo/status');
      setStatus(data);
    } catch { setStatus({ is_demo: false }); }
  };

  useEffect(() => {
    reload();
    const tid = setInterval(reload, 5 * 60 * 1000); // every 5 min
    return () => clearInterval(tid);
  }, []);

  // Restore dismissed state (per-session — only for non-critical banners)
  useEffect(() => {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (until && Date.now() < until) setDismissed(true);
  }, []);

  if (!status?.is_demo) return null;

  const days = status.days_left ?? 0;
  const expired = status.is_expired || status.subscription_status === 'trial_expired';
  // Allow dismissing the banner ONLY when more than 3 days are left
  const dismissible = !expired && days > 3;
  if (dismissed && dismissible) return null;

  const tone =
    expired ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' :
    days <= 1 ? 'bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]' :
    days <= 3 ? 'bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]' :
                 'bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]';

  const requestExtension = async () => {
    setRequesting(true);
    try {
      const { data } = await api.post('/demo/request-extension');
      toast({
        title: data.already_pending ? 'Demande déjà en attente' : 'Demande envoyée',
        description: 'Notre équipe vous recontacte sous 24h ouvrées.',
      });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Envoi impossible' });
    } finally { setRequesting(false); }
  };

  const handleDismiss = () => {
    // Re-show in 24h
    localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 3600 * 1000));
    setDismissed(true);
  };

  return (
    <div
      data-testid="demo-countdown-banner"
      className={`w-full border-b ${tone} px-4 py-2.5 flex items-center justify-center gap-3 text-[12.5px] flex-wrap`}
    >
      {expired ? (
        <>
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span><strong>Votre essai a expiré.</strong> Vos données sont en lecture seule. Activez votre abonnement pour les conserver.</span>
        </>
      ) : (
        <>
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span>
            Période d'essai gratuite — <strong>{days} jour{days > 1 ? 's' : ''} restant{days > 1 ? 's' : ''}</strong> sur 15.
            {' '}Vos données sont conservées si vous passez en abonnement.
          </span>
        </>
      )}
      <button
        onClick={requestExtension}
        disabled={requesting}
        data-testid="demo-banner-request-extension"
        className="h-7 px-3 rounded-full bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-[11.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
      >
        {requesting ? <Loader2 className="w-3 h-3 animate-spin" /> : <MailPlus className="w-3 h-3" />}
        Demander une prolongation
      </button>
      <a
        href="mailto:contact@ofmanager.fr?subject=Conversion%20vers%20abonnement%20OFManager"
        data-testid="demo-banner-subscribe-cta"
        className="h-7 px-3 rounded-full bg-[#4F46E5] hover:bg-[#4338CA] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 no-underline"
      >
        S'abonner
      </a>
      {dismissible && (
        <button
          onClick={handleDismiss}
          aria-label="Masquer le bandeau"
          className="ml-1 opacity-70 hover:opacity-100"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}
